import { ChartEditor } from '@/modules/imports/container/scan-discs-popup-container/editor/useChartEditor'
import * as React from 'react'
import { ChartScanActivity } from '@/lib/api/company/chartScan'
import {
  DayPiece,
  clockMinute,
  dayOffset,
  offsetToIso,
} from '@/modules/imports/container/scan-discs-popup-container/editor/chartDay'
import { useVehicleLabels } from '@/modules/imports/container/scan-discs-popup-container/editor/useVehicleLabels'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getDriverActivitiesColumns } from '@/modules/drivers/constants/tables'
import { ActivitiesEmptyState } from '@/modules/shared/components/ActivitiesEmptyState'
import { ActivityListHeader } from '../../components/lists/ActivityListHeader'
import ActivityListRow from '../../components/lists/ActivityListRow'


const TachoDiscActivites = ({
  pieces,
  editor,
  companyId,
  onContextMenu,
}: {
  pieces: DayPiece[]
  editor: ChartEditor
  companyId: string
  onContextMenu: (activity: ChartScanActivity, position: { x: number; y: number }) => void
}) => {
  const { t } = useLocalize()
  const columns = getDriverActivitiesColumns(t)
  const vehicleLabels = useVehicleLabels(companyId)

  const sortedActivities = React.useMemo(() => {
    const dayStart = clockMinute(editor.dayStartDate)
    // Vehicles in the order they take over, the first one from the day start.
    const vehicles = [
      {
        offset: 0,
        vehicleId: editor.vehicleId,
        odometerStart: editor.firstVehicleOdometerStart,
        odometerEnd: editor.firstVehicleOdometerEnd,
      },
      ...editor.additionalVehiclesUsed
        .map((v) => ({
          offset: dayOffset(clockMinute(v.startTime), dayStart),
          vehicleId: v.vehicleId ?? null,
          odometerStart: v.odometerStart,
          odometerEnd: v.odometerEnd,
        }))
        .sort((x, y) => x.offset - y.offset),
    ]
    return pieces.map((piece, index) => {
      const vehicle = [...vehicles].reverse().find((v) => v.offset <= piece.start)
      const label = vehicle?.vehicleId != null ? vehicleLabels.get(vehicle.vehicleId) : undefined
      // WPF "Zmiana pojazdu" column: the row a vehicle starts on carries its odometer.
      const startsHere = vehicle != null && (index === 0 || vehicle.offset === piece.start)
      return {
        id: piece.activity.activityId,
        key: `${piece.activity.activityId}-${piece.start}`,
        source: piece.activity,
        type: piece.activity.type,
        // Real date-time: a part after midnight shows the next calendar day.
        startTime: offsetToIso(editor.dayStartDate, piece.start),
        endTime: offsetToIso(editor.dayStartDate, piece.end),
        isCrew: piece.activity.crew,
        isFerryCrossing: piece.activity.ferryTrainCrossing,
        isManualEntry: piece.activity.manualEntry,
        isOutOfScope: false,
        registrationNumber:
          label && startsHere
            ? `${label} (${vehicle.odometerStart} – ${vehicle.odometerEnd})`
            : label,
        coDriver: undefined,
      }
    })
  }, [
    pieces,
    vehicleLabels,
    editor.dayStartDate,
    editor.vehicleId,
    editor.firstVehicleOdometerStart,
    editor.firstVehicleOdometerEnd,
    editor.additionalVehiclesUsed,
  ])

  return (
    <section className="tw-flex-1 tw-overflow-auto">
      <div className="tw-relative tw-h-full">
        {sortedActivities.length > 0 ? (
          <div className="tw-h-full tw-overflow-y-auto">
            <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2 tw-pl-2">
              <ActivityListHeader columns={columns} />
              <tbody>
                {sortedActivities.map((activity) => (
                  <ActivityListRow
                    key={activity.key}
                    activity={activity}
                    onMouseEnter={() => editor.setHovered(activity.id, false)}
                    onMouseLeave={() => editor.setHovered(null, false)}
                    isHovered={editor.hoveredActivityId === activity.id}
                    isSelected={editor.selectedActivityIds.has(activity.id)}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {
                        const ids = new Set(editor.selectedActivityIds)
                        if (ids.has(activity.id)) ids.delete(activity.id)
                        else ids.add(activity.id)
                        editor.setSelected([...ids])
                      } else editor.setSelected([activity.id])
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      onContextMenu(activity.source, { x: e.clientX, y: e.clientY })
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tw-grid tw-h-full tw-min-h-[min(100%,420px)] tw-w-full tw-place-items-center tw-px-4">
            <ActivitiesEmptyState
              title={t('dailyActivities.empty-activities-title')}
              subtitle={t('dailyActivities.empty-activities-subtitle')}
            />
          </div>
        )}
      </div>
    </section>
  )
}

export default TachoDiscActivites
